import React, { useState, useRef, useEffect } from 'react'
import { BiChevronDown } from 'react-icons/bi'
import { AiOutlineSearch } from 'react-icons/ai'
import { MdAddBox } from 'react-icons/md'
import { HiCheck } from 'react-icons/hi'
import { useCurrent } from '../../hooks'
import CustomerSelectorItem from '../CustomerSelectorItem'

const MwSelector = ({
  label,
  _data,
  dataType,
  onClickNew,
  setSelectedItem,
  selectedItem,
  initalValue = 'Choose',
  withSearch = true,
  withLabel = true,
  withAddNew = true,
  className,
  styleList,
  invalid,
  invalidMsg,
}) => {
  const [inputValue, setInputValue] = useState('')
  const { currentLangId } = useCurrent()
  const [open, setOpen] = useState(false)
  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }
  }, [open])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        open &&
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const onSelectedHandler = (item) => {
    setSelectedItem({ ...item, label: item.label })
    setOpen(false)
    setInputValue('')
  }

  const dataList =
    _data &&
    _data.map((item, index) => {
      if (dataType === 'customer' || dataType === 'salesman') {
        return (
          <CustomerSelectorItem
            key={item._id}
            item={item}
            setSelectedItem={setSelectedItem}
            setOpen={setOpen}
            setInputValue={setInputValue}
            inputValue={inputValue}
          />
        )
      }

      if (dataType === 'labelValue') {
        const isCurrentLang = item?.lang && item.lang.trim() === currentLangId
        const visible = isCurrentLang
          ? item?.label?.toLowerCase().startsWith(inputValue)
          : item?.label?.toLowerCase().includes(inputValue.toLowerCase())

        const isActive = selectedItem?.value === item.value

        return (
          <div
            key={index}
            className={`
              flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors
              ${visible ? 'flex' : 'hidden'}
              ${isActive
                ? 'text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-950/30'
                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
              }
            `}
            onClick={() => onSelectedHandler(item)}
          >
            <span className={`w-4 h-4 flex-shrink-0 transition-opacity ${isActive ? 'opacity-100' : 'opacity-0'}`}>
              <HiCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </span>
            {item.label}
          </div>
        )
      }

      if (dataType === 'salesmansTypes') {
        const displayText =
          item.typeName.find((name) => name.lang === currentLangId)?.text ||
          item.typeName[0]?.text ||
          'No Name'
        const itemWithLabel = { ...item, label: displayText }
        const visible = displayText.toLowerCase().includes(inputValue.toLowerCase())
        const isActive = selectedItem?._id === item._id

        return (
          <div
            key={item._id}
            className={`
              flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors
              ${visible ? 'flex' : 'hidden'}
              ${isActive
                ? 'text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-950/30'
                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
              }
            `}
            onClick={() => onSelectedHandler(itemWithLabel)}
          >
            <span className={`w-4 h-4 flex-shrink-0 ${isActive ? 'opacity-100' : 'opacity-0'}`}>
              <HiCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </span>
            {displayText}
          </div>
        )
      }
    })

  const displayLabel = () => {
    if (!selectedItem || selectedItem === 'sub') return null
    if (dataType === 'salesmansTypes' && selectedItem.typeName) {
      return (
        selectedItem.typeName.find((name) => name.lang === currentLangId)?.text ||
        selectedItem.typeName[0]?.text ||
        selectedItem.label
      )
    }
    return selectedItem.label
  }

  const currentLabel = displayLabel()

  const visibleCount = _data
    ? _data.filter((item) => {
        if (dataType === 'labelValue') {
          const isCurrentLang = item?.lang && item.lang.trim() === currentLangId
          return isCurrentLang
            ? item?.label?.toLowerCase().startsWith(inputValue)
            : item?.label?.toLowerCase().includes(inputValue.toLowerCase())
        }
        if (dataType === 'salesmansTypes') {
          const text = item.typeName?.find((n) => n.lang === currentLangId)?.text || item.typeName?.[0]?.text || ''
          return text.toLowerCase().includes(inputValue.toLowerCase())
        }
        return true
      }).length
    : 0

  return (
    <div className={`relative ${className}`}>
      {withLabel && label && (
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
          {label}
        </label>
      )}

      <div
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className={`
          flex items-center gap-2 h-[38px] px-3 w-full
          bg-white dark:bg-slate-800
          border dark:border-slate-600
          rounded-lg
          transition-all duration-150 cursor-pointer
          ${open
            ? 'border-indigo-400 ring-2 ring-indigo-100 dark:ring-indigo-900/40'
            : invalid
              ? 'border-red-400 ring-2 ring-red-100 dark:ring-red-900/30'
              : 'border-slate-200 hover:border-slate-300 dark:hover:border-slate-500'
          }
          ${styleList}
        `}
      >
        <span className={`flex-1 text-start text-sm ${currentLabel ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'}`}>
          {currentLabel || initalValue}
        </span>
        <BiChevronDown
          className={`flex-shrink-0 text-slate-400 dark:text-slate-500 text-lg transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </div>

      {invalid && invalidMsg && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">{invalidMsg}</p>
      )}

      {open && (
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 shadow-lg overflow-hidden"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px`, width: `${dropdownPosition.width}px` }}
        >
          {/* Header: search + add new button — same pattern as MwSelectorMulti */}
          {withSearch && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80">
              <AiOutlineSearch className="text-slate-400 dark:text-slate-500 text-base flex-shrink-0" />
              <input
                autoFocus
                type="text"
                className="flex-1 text-sm bg-transparent border-none outline-none text-slate-700 dark:text-slate-200 placeholder-slate-400"
                placeholder="Search…"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              {/* Smart hint: show match count while typing */}
              {inputValue && visibleCount > 0 && (
                <span className="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0 tabular-nums">
                  {visibleCount}
                </span>
              )}
              {withAddNew && onClickNew && (
                <button
                  type="button"
                  onClick={() => { onClickNew(); setOpen(false) }}
                  className="text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 transition-colors flex-shrink-0"
                  title="Add new"
                >
                  <MdAddBox className="text-xl" />
                </button>
              )}
            </div>
          )}

          <div className="max-h-56 overflow-y-auto divide-y divide-slate-50 dark:divide-slate-700/50">
            {dataList}
            {/* Smart empty state when search yields nothing */}
            {inputValue && visibleCount === 0 && (
              <div className="flex flex-col items-center gap-2 py-6 px-4">
                <span className="text-2xl">🔍</span>
                <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
                  No results for <span className="font-medium text-slate-600 dark:text-slate-300">"{inputValue}"</span>
                </p>
                {withAddNew && onClickNew && (
                  <button
                    type="button"
                    onClick={() => { onClickNew(); setOpen(false) }}
                    className="mt-1 text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                  >
                    + Add "{inputValue}" as new
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MwSelector